import React, { useState, useEffect } from "react";
import { Box, Typography } from "../../../design-system";
import logoSite from "../../../../assets/brand/logo_site.svg";
import DownloadIcon from "@mui/icons-material/Download";
import MovieIcon from "@mui/icons-material/Movie";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import ContentCutIcon from "@mui/icons-material/ContentCut";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";

/**
 * Letter J — a MARKETING landing page (vs the full demo on B). Goal per the Yosef brief:
 * inform → intrigue → capture waitlist emails. Message + email capture lead; a simplified,
 * mobile-style clip demo is the "wow" (tap clips → export), not the full editor. Purpose-built
 * so it's fast, clean and unbreakable. (Waitlist is captured client-side for now — wire the
 * backend endpoint next.)
 */
const PINK = "#e61d50";
const F = "Outfit, sans-serif";
const ANTON = "'Anton', Outfit, sans-serif";

const injectOnce = () => {
  if (typeof document === "undefined" || document.getElementById("cc-mkt-fonts")) return;
  const l = document.createElement("link");
  l.id = "cc-mkt-fonts";
  l.rel = "stylesheet";
  l.href = "https://fonts.googleapis.com/css2?family=Anton&family=Outfit:wght@400;500;600;700;800&family=JetBrains+Mono:wght@600&display=swap";
  document.head.appendChild(l);
  const s = document.createElement("style");
  s.id = "cc-mkt-kf";
  s.textContent = "@keyframes ccUp{from{opacity:0;transform:translateY(16px)}to{opacity:1;transform:none}}";
  document.head.appendChild(s);
};

const up = (delay = 0) => ({ animation: `ccUp .6s cubic-bezier(.2,.7,.2,1) both`, animationDelay: `${delay}s` });

// ── waitlist capture (client-side for now) ─────────────────────────────────
const WaitlistForm: React.FC<{ variant?: "hero" | "band" }> = ({ variant = "hero" }) => {
  const [email, setEmail] = useState("");
  const [done, setDone] = useState(false);
  const valid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
  const submit = () => {
    if (!valid) return;
    try {
      const key = "cc_waitlist";
      const list = JSON.parse(localStorage.getItem(key) || "[]");
      if (!list.includes(email.trim().toLowerCase())) list.push(email.trim().toLowerCase());
      localStorage.setItem(key, JSON.stringify(list));
    } catch (e) { /* ignore */ }
    setDone(true);
  };
  if (done) return (
    <Box sx={{ display: "flex", alignItems: "center", gap: "10px", p: "14px 18px", borderRadius: "12px", border: `1px solid ${PINK}66`, background: "rgba(230,29,80,0.1)", maxWidth: 460 }}>
      <Box sx={{ fontSize: "20px" }}>🍒</Box>
      <Typography sx={{ fontFamily: F, fontWeight: 700, color: "#fff", fontSize: "15px" }}>You're on the list — we'll email you when your seat opens.</Typography>
    </Box>
  );
  return (
    <Box sx={{ maxWidth: 460, width: "100%" }}>
      <Box sx={{ display: "flex", gap: "8px", background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.16)", borderRadius: "12px", p: "6px", "&:focus-within": { borderColor: PINK } }}>
        <input
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") submit(); }}
          placeholder="you@work.com"
          style={{ flex: 1, minWidth: 0, background: "transparent", border: "none", outline: "none", color: "#fff", fontFamily: F, fontSize: "15px", padding: "10px 12px" }}
        />
        <Box component="button" onClick={submit} sx={{ cursor: "pointer", border: "none", borderRadius: "9px", px: "18px", py: "11px", display: "flex", alignItems: "center", gap: "6px", fontFamily: F, fontWeight: 800, fontSize: "14px", color: "#fff", background: `linear-gradient(100deg,#ff4d7d,${PINK})`, whiteSpace: "nowrap", opacity: valid ? 1 : 0.7, transition: "filter .15s", "&:hover": { filter: "brightness(1.08)" } }}>
          Get early access <ArrowForwardIcon sx={{ fontSize: 17 }} />
        </Box>
      </Box>
      <Typography sx={{ fontFamily: F, fontSize: "12.5px", color: "#8d94a8", mt: "8px", px: "4px" }}>{variant === "hero" ? "Free early access, opening in waves. No spam — one email when you're in." : "One email when your seat's ready."}</Typography>
    </Box>
  );
};

// ── the simple, mobile-style clip demo (the "wow") ─────────────────────────
type Clip = { id: number; text: string; tone: "pos" | "neu"; a: number; b: number };
const CLIPS: Clip[] = [
  { id: 1, text: "So every story has its cherries — pick yours.", tone: "pos", a: 0, b: 2.3 },
  { id: 2, text: "This is the future of video editing.", tone: "neu", a: 2.3, b: 4.0 },
  { id: 3, text: "Your hierarchy is really good — it pulls me in.", tone: "pos", a: 4.0, b: 7.1 },
  { id: 4, text: "The call to action is clear.", tone: "pos", a: 16.3, b: 17.8 },
  { id: 5, text: "And that changes everything about how you watch.", tone: "neu", a: 19.3, b: 21.3 },
];
const tc = (s: number) => `00:${String(Math.floor(s / 60)).padStart(2, "0")}:${String(Math.floor(s % 60)).padStart(2, "0")}`;
const TONE = { pos: { c: "#22c55e", label: "POSITIVE" }, neu: { c: "#6aa8ff", label: "NEUTRAL" } } as const;

const SimpleDemo: React.FC = () => {
  const [rows, setRows] = useState<Clip[]>(CLIPS);
  const [picks, setPicks] = useState<number[]>([]);
  const [toast, setToast] = useState("");
  const say = (m: string) => { setToast(m); window.clearTimeout((say as any)._t); (say as any)._t = window.setTimeout(() => setToast(""), 3000); };
  const pick = (id: number) => setPicks((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));
  const trim = (id: number, edge: "a" | "b", d: number) => setRows((rs) => rs.map((r) => r.id === id ? (edge === "a" ? { ...r, a: Math.max(0, Math.min(r.b - 0.5, r.a + d)) } : { ...r, b: Math.max(r.a + 0.5, r.b + d) }) : r));
  const total = picks.reduce((s, id) => { const r = rows.find((x) => x.id === id); return s + (r ? Math.round(r.b - r.a) : 0); }, 0);

  return (
    <Box sx={{ maxWidth: 560, mx: "auto", width: "100%" }}>
      {/* instruction */}
      <Box sx={{ display: "flex", alignItems: "center", gap: "14px", flexWrap: "wrap", mb: "14px", p: "12px 16px", borderRadius: "12px", border: `1px solid ${PINK}44`, background: "rgba(230,29,80,0.08)" }}>
        <Typography sx={{ fontFamily: F, fontSize: "13.5px", color: "#f3f4f9", fontWeight: 600 }}><Box component="span" sx={{ color: PINK, fontWeight: 800 }}>①</Box> Tap clips to pick them &nbsp; <Box component="span" sx={{ color: PINK, fontWeight: 800 }}>②</Box> Press Export → one video</Typography>
      </Box>

      {/* phone-ish card */}
      <Box sx={{ borderRadius: "18px", border: "1px solid rgba(255,255,255,0.12)", background: "#0d0b12", overflow: "hidden", boxShadow: "0 40px 90px -50px rgba(0,0,0,0.85)" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: "8px", px: "16px", py: "12px", borderBottom: "1px solid rgba(255,255,255,0.08)" }}>
          <img src={logoSite} alt="" width={20} />
          <Typography sx={{ fontFamily: F, fontWeight: 800, fontSize: "14px", color: "#fff" }}>CherryClipz</Typography>
          <Box sx={{ flex: 1 }} />
          <Typography sx={{ fontFamily: "'JetBrains Mono',monospace", fontSize: "12px", color: picks.length ? PINK : "#6b6675" }}>{picks.length ? `${picks.length} clip${picks.length === 1 ? "" : "s"} · ${tc(total)}` : `${rows.length} clips`}</Typography>
        </Box>
        {rows.map((r, i) => {
          const order = picks.indexOf(r.id);
          const on = order >= 0;
          return (
            <Box key={r.id} onClick={() => pick(r.id)} sx={{ cursor: "pointer", display: "flex", gap: "12px", p: "12px 14px", borderBottom: i < rows.length - 1 ? "1px solid rgba(255,255,255,0.05)" : "none", background: on ? "rgba(230,29,80,0.08)" : "transparent", transition: "background .12s", "&:hover": { background: on ? "rgba(230,29,80,0.12)" : "rgba(255,255,255,0.02)" } }}>
              {/* checkbox */}
              <Box sx={{ flexShrink: 0, alignSelf: "center", width: 26, height: 26, borderRadius: "7px", border: `2px solid ${on ? PINK : "rgba(255,255,255,0.3)"}`, background: on ? PINK : "transparent", display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontFamily: F, fontWeight: 800, fontSize: "12px" }}>{on ? order + 1 : ""}</Box>
              {/* thumbnail */}
              <Box sx={{ flexShrink: 0, width: 64, height: 40, borderRadius: "7px", background: "linear-gradient(145deg,#1a1420,#0d0a12)", border: "1px solid rgba(255,255,255,0.07)", display: "flex", alignItems: "center", justifyContent: "center" }}><img src={logoSite} alt="" width={16} style={{ opacity: 0.8 }} /></Box>
              {/* transcript + tone */}
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontFamily: F, fontSize: "14px", color: "#f3f4f9", lineHeight: 1.35 }}>{r.text}</Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: "8px", mt: "5px" }}>
                  <Box component="span" sx={{ fontFamily: F, fontSize: "8.5px", fontWeight: 800, letterSpacing: "0.05em", color: "#fff", background: TONE[r.tone].c, borderRadius: "999px", px: "7px", py: "2px" }}>{TONE[r.tone].label}</Box>
                  <Typography sx={{ fontFamily: F, fontSize: "11px", color: "#6b6675" }}>{Math.round(r.b - r.a)}s</Typography>
                </Box>
              </Box>
              {/* timecode ± + download */}
              <Box onClick={(e: React.MouseEvent) => e.stopPropagation()} sx={{ flexShrink: 0, display: "flex", flexDirection: "column", gap: "3px", alignItems: "flex-end" }}>
                {(["a", "b"] as const).map((edge) => (
                  <Box key={edge} sx={{ display: "flex", alignItems: "center", gap: "3px" }}>
                    <Box component="button" onClick={() => trim(r.id, edge, -0.5)} sx={stepSx}>−</Box>
                    <Typography sx={{ fontFamily: "'JetBrains Mono',monospace", fontSize: "10.5px", color: edge === "a" ? "#c2c7d6" : "#f5a524", minWidth: 52, textAlign: "center" }}>{tc(edge === "a" ? r.a : r.b)}</Typography>
                    <Box component="button" onClick={() => trim(r.id, edge, 0.5)} sx={stepSx}>+</Box>
                  </Box>
                ))}
                <Box component="button" onClick={() => say("Download (demo): saves this clip to your device.")} sx={{ ...stepSx, border: "none", mt: "2px", color: "#8d94a8", "&:hover": { color: PINK } }}><DownloadIcon sx={{ fontSize: 15 }} /></Box>
              </Box>
            </Box>
          );
        })}
      </Box>

      {/* export bar */}
      <Box sx={{ display: "flex", gap: "10px", mt: "14px", alignItems: "center", flexWrap: "wrap" }}>
        <Box component="button" onClick={() => say(picks.length ? `Exporting your ${picks.length}-clip reel (${tc(total)}) — demo.` : "Tap a clip first, then Export.")} sx={{ cursor: "pointer", border: "none", borderRadius: "11px", px: "20px", py: "12px", display: "flex", alignItems: "center", gap: "8px", fontFamily: F, fontWeight: 800, fontSize: "15px", color: "#fff", background: `linear-gradient(100deg,#ff4d7d,${PINK})`, "&:hover": { filter: "brightness(1.08)" } }}><MovieIcon sx={{ fontSize: 19 }} /> Export {picks.length ? `${picks.length} clips` : ""} → one video</Box>
        <Typography sx={{ fontFamily: F, fontSize: "13px", color: "#8d94a8" }}>or tap ↓ on a clip to grab just that one</Typography>
      </Box>
      {toast && <Box sx={{ mt: "12px", px: "14px", py: "10px", borderRadius: "10px", background: "rgba(230,29,80,0.1)", border: `1px solid ${PINK}44`, color: "#f3f4f9", fontFamily: F, fontSize: "13px" }}>💡 {toast}</Box>}
    </Box>
  );
};
const stepSx = { cursor: "pointer", width: 18, height: 18, borderRadius: "5px", border: "1px solid rgba(255,255,255,0.16)", background: "transparent", color: "#c2c7d6", display: "flex", alignItems: "center", justifyContent: "center", p: 0, fontSize: "12px", lineHeight: 1 } as const;

// ── how it works ───────────────────────────────────────────────────────────
const STEPS = [
  { Icon: ContentCutIcon, t: "We cut everything", d: "Every sentence — and every silence — becomes its own clip, tagged with speaker, emotion & keywords." },
  { Icon: FilterAltIcon, t: "Filter like a spreadsheet", d: "Sort and search your whole video. Only the angry lines. Only under 3 seconds. Only what mentions “launch.”" },
  { Icon: AutoFixHighIcon, t: "Cherry-pick, or ask AI", d: "Tick the moments you want in any order — or let Black Cherry AI build the reel. Export one video." },
];

const MarketingLandingPage: React.FC = () => {
  useEffect(() => { injectOnce(); }, []);
  return (
    <Box sx={{ fontFamily: F, color: "#f3f4f9", background: "radial-gradient(120% 60% at 50% 0%, rgba(230,29,80,0.10), transparent 55%), linear-gradient(180deg,#0c0b10,#08070b)", minHeight: "100vh" }}>
      <Box sx={{ maxWidth: 1180, mx: "auto", px: { xs: "20px", md: "32px" } }}>

        {/* HERO */}
        <Box sx={{ pt: { xs: "48px", md: "88px" }, pb: { xs: "40px", md: "64px" }, display: "grid", gridTemplateColumns: { xs: "1fr", md: "1.05fr 0.95fr" }, gap: { xs: "40px", md: "56px" }, alignItems: "center" }}>
          <Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: "10px", mb: "22px", ...up(0) }}>
              <img src={logoSite} alt="CherryClipz" width={34} />
              <Typography sx={{ fontFamily: F, fontWeight: 700, letterSpacing: "0.16em", fontSize: "12px", color: "#8d94a8", textTransform: "uppercase" }}>Video → a spreadsheet of clips</Typography>
            </Box>
            <Typography sx={{ fontFamily: ANTON, fontSize: { xs: "46px", md: "72px" }, lineHeight: 0.98, letterSpacing: "0.01em", color: "#fff", ...up(0.05) }}>
              Opus Clip,<br />but <Box component="span" sx={{ color: PINK }}>you're driving.</Box>
            </Typography>
            <Typography sx={{ fontFamily: F, fontSize: { xs: "16px", md: "19px" }, color: "#c2c7d6", lineHeight: 1.55, mt: "18px", maxWidth: 520, ...up(0.12) }}>
              Your video becomes a <Box component="span" sx={{ color: "#fff", fontWeight: 700 }}>searchable spreadsheet of clips</Box>. Cherry-pick the best moments — or let AI. No timeline, no black box.
            </Typography>
            <Box sx={{ mt: "26px", ...up(0.18) }}><WaitlistForm variant="hero" /></Box>
          </Box>
          {/* hero demo teaser */}
          <Box sx={{ ...up(0.1) }}><SimpleDemo /></Box>
        </Box>

        {/* HOW IT WORKS */}
        <Box sx={{ py: { xs: "40px", md: "64px" } }}>
          <Typography sx={{ fontFamily: ANTON, fontSize: { xs: "30px", md: "42px" }, color: "#fff", textAlign: "center", mb: "8px" }}>How it works</Typography>
          <Typography sx={{ fontFamily: F, fontSize: "16px", color: "#9aa0b4", textAlign: "center", mb: "36px" }}>One upload. A hundred clips. You keep the cherry on top.</Typography>
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(3,1fr)" }, gap: "16px" }}>
            {STEPS.map((s, i) => (
              <Box key={s.t} sx={{ p: "24px", borderRadius: "16px", border: "1px solid rgba(255,255,255,0.09)", background: "rgba(255,255,255,0.02)", transition: "transform .2s, border-color .2s", "&:hover": { transform: "translateY(-3px)", borderColor: `${PINK}66` } }}>
                <Box sx={{ width: 46, height: 46, borderRadius: "12px", border: `1px solid ${PINK}55`, background: "rgba(230,29,80,0.12)", display: "flex", alignItems: "center", justifyContent: "center", color: PINK, mb: "16px" }}><s.Icon sx={{ fontSize: 24 }} /></Box>
                <Typography sx={{ fontFamily: F, fontWeight: 800, fontSize: "18px", color: "#fff", mb: "6px" }}><Box component="span" sx={{ color: PINK }}>{i + 1}.</Box> {s.t}</Typography>
                <Typography sx={{ fontFamily: F, fontSize: "14px", color: "#9aa0b4", lineHeight: 1.55 }}>{s.d}</Typography>
              </Box>
            ))}
          </Box>
        </Box>

        {/* EARNED WAITLIST BAND */}
        <Box sx={{ my: { xs: "40px", md: "72px" }, p: { xs: "32px 24px", md: "48px" }, borderRadius: "22px", border: `1px solid ${PINK}55`, background: "linear-gradient(120deg, rgba(230,29,80,0.16), rgba(255,255,255,0.02))", textAlign: "center" }}>
          <Typography sx={{ fontFamily: ANTON, fontSize: { xs: "30px", md: "46px" }, color: "#fff", lineHeight: 1.05 }}>Do it with <Box component="span" sx={{ color: PINK }}>your</Box> video.</Typography>
          <Typography sx={{ fontFamily: F, fontSize: { xs: "15px", md: "17px" }, color: "#e9d3da", mt: "12px", mb: "26px", maxWidth: 560, mx: "auto" }}>You're early — uploads open in waves. Drop your email and skip the line.</Typography>
          <Box sx={{ display: "flex", justifyContent: "center" }}><WaitlistForm variant="band" /></Box>
        </Box>

        {/* footer */}
        <Box sx={{ py: "32px", borderTop: "1px solid rgba(255,255,255,0.06)", display: "flex", alignItems: "center", gap: "10px" }}>
          <img src={logoSite} alt="" width={22} />
          <Typography sx={{ fontFamily: F, fontWeight: 800, fontSize: "15px", color: "#fff" }}>CherryClipz</Typography>
          <Box sx={{ flex: 1 }} />
          <Typography sx={{ fontFamily: F, fontSize: "12.5px", color: "#6b6675" }}>Your video, as a spreadsheet of clips. 🍒</Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default MarketingLandingPage;
